import { AppBar, Box, Button, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EmptyList,
	EnhancedButtonPrimeReact,
	EnhancedButtonPrimeReactSeverity,
	EnhancedDialog,
	EnhancedDropDownRHF,
	EnhancedSnackbar,
	IDNDList,
	RHookFormNumberField,
	RouterLinkButton,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	PAYROLL_ADJUSTMENT_LIST_SCREEN_SLUG,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { IResponse, IServerError } from "../../models/common";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { clearSelectedINVOICE, setSelectedINVOICE } from "../../store/actions/invoiceReportActions";
import { formatDate, formatThousandSeperatorWithTwoFixedAmount, savePdfAsFile, sortDDLByLabel, useNavigateToLogout } from "../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedDrawerMenu,
	setSelectedStickyNavBar,
} from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminClientRoutesComponent } from "../RoleBasedLeftMenu/routes/AdminClientRoutes";
import { CalculatedPayrollAdjustmentDataGrid } from "./CalculatedPayrollAdjustmentDataGrid";
import { CalculatedPayrollAdjustmentSetupHeaderPanel } from "./Panels/CalculatedPayrollAdjustmentSetupHeaderPanel";
import { CalculatedPayrollAdjustmentSetupToolbar } from "./CalculatedPayrollAdjustmentSetupToolbar";
import { ClientRoutesComponent } from "../RoleBasedLeftMenu/routes/ClientRoutes";
import { CountryService } from "../../services/common/CountryService";
import { DepartmentsService } from "../../services/DepartmentsService";
import { EmployeeService } from "../../services/EmployeeService";
import { IAppState } from "../../store/reducers";
import { IGetListRequest } from "../../models";
import { MoveEmployeeFirstPanel } from "./Panels/MoveEmployeeFirstPanel";
import { MoveEmployeeSecondPanel } from "./Panels/MoveEmployeeSecondPanel";
import { PayrollAdjustmentAddEmployeePanel } from "./Panels/PayrollAdjustmentAddEmployeePanel";
import { PayrollAdjustmentDataGrid } from "./PayrollAdjustmentDataGrid";
import { PayrollAdjustmentSettingEmployeeDataTable } from "./Panels/PayrollAdjustmentSettingEmployeePanel";
import { PayrollAdjustmentSetupHeaderPanel } from "./Panels/PayrollAdjustmentSetupHeaderPanel";
import { PayrollAdjustmentSetupToolbar } from "./PayrollAdjustmentSetupToolbar";
import { PayrollService } from "../../services/PayrollService";
import { PrimeDataGridWithColumnToggled } from "../PrimeDataGridWithColumnToggled";
import { SalaryProfileService } from "../../services/SalaryProfileService";
import { instanceOfIPayrollAdjustmentAddEmployeeResponsePayload } from "../../common/instance-method";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { ClientService } from "../../services";
import { GeneralSettingsService } from "../../services/GeneralSettingsService";

export interface IPayroll { }
const PAYROLL_ADJUSTMENT_LIST_QUERY_KEY = "payroll-list";
const CALCULATED_PAYROLL_ADJUSTMENT_LIST_QUERY_KEY = "calculated-payroll-list";
const PAY_FREQUENCY_DDL_LIST_QUERY_KEY = "pay-frequency-ddl";
const PAYROLL_PERIODS_DDL_LIST_QUERY_KEY = "payroll-periods-ddl";
const DEPARTMENTS_DDL_LIST_QUERY_KEY = "department-ddl";
const ASSIGN_AND_UNASSIGNED_EMPLOYEE_DATA_QUERY_KEY = "assigned-unassinged-employee-data-record";
const SETTING_EMPLOYEE_FIELDS_EMPLOYEE_DATA_QUERY_KEY = "setting-employee-fields-data-record";

const useGetPayFrequencyDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: [PAY_FREQUENCY_DDL_LIST_QUERY_KEY, clientId, taxYear],
		queryFn: (data) => {
			return EmployeeService.getPayFrequencyDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

const useGetPayrollPeriodsDDL = (
	clientId: string, payPeriodId: string | null, taxYear: number
) =>
	useQuery({
		queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, payPeriodId, taxYear],
		queryFn: (data) => {
			return PayrollService.getPayrollPeriodsDDL(clientId, payPeriodId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

const useGetVocationalEarningsList = (clientId: string) =>
	useQuery({
		queryKey: ["vocational-earnings-list"],
		queryFn: () => {
			return PayrollService.getVocationalEarningsList(clientId);
		},
		refetchOnWindowFocus: false,
	});
const useGetDepartmentsDDL = (clientId: string) =>
	useQuery({
		queryKey: ["deaprtments-ddl", clientId],
		queryFn: () => {
			return DepartmentsService.getGetDepartmentsDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});
const useGetPayrollAdjustmentTableData = (
	clientId: string,
	payPeriodFrequencyId: string | null,
	payPeriodStarDate: string | null,
	payPeriodEndDate: string | null,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [
			PAYROLL_ADJUSTMENT_LIST_QUERY_KEY,
			clientId,
			payPeriodFrequencyId,
			payPeriodStarDate,
			payPeriodEndDate,
			params
		],
		queryFn: (data) => {
			return PayrollService.getPayrollAdjustment(
				clientId,
				payPeriodFrequencyId,
				payPeriodStarDate,
				payPeriodEndDate,
				params,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});


const useGetCalculatedPayrollAdjustmentTableData = (
	clientId: string,
	payPeriodFrequencyId: string | null,
	payPeriodStarDate: string | null,
	payPeriodEndDate: string | null,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [
			CALCULATED_PAYROLL_ADJUSTMENT_LIST_QUERY_KEY,
			clientId,
			payPeriodFrequencyId,
			payPeriodStarDate,
			payPeriodEndDate,
			params
		],
		queryFn: (data) => {

			return PayrollService.getCalculatedPayrollAdjustment(
				clientId,
				payPeriodFrequencyId,
				payPeriodStarDate,
				payPeriodEndDate,
				params,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

const EDR_EARNINGS_AND_TIME_FORMAT_DATA_QUERY_KEY =
	"edr-earnings-single-data-record";
const useGetEDREarningsAndTimeFormatData = (
	clientId: string,
	taxYear: number
) =>
	useQuery({
		queryKey: [EDR_EARNINGS_AND_TIME_FORMAT_DATA_QUERY_KEY],
		queryFn: (data) => {
			return SalaryProfileService.getEDREarningsAndTimeFormatData(
				clientId,
				taxYear
			);
		},
		refetchOnWindowFocus: false,
	});

const moveEmployeeFirstPanelUpdatedColumns = [
	{
		field: "name",
		headerName: `Select / Un-select All Employee(s)`,
		description: "",
		flex: 1,
		hide: false,
		hideable: false,
		sortable: true,
		sortingOrder: "asc",
		resizable: false,
		type: "string",
		align: "left",
		headerAlign: "left",
		hideSortIcons: false,
		disableColumnMenu: true,
		disableReorder: true,
		disableExport: true,
		maskable: false,
		isFixed: true,
		sequence: 0,
		frozenColumn: false,
		footer: null,
	},
];

const useGetPayrollAdjustmentAddEmployeePanels = (
	clientId: string,
	taxYear: number,
	selectedPayPeriodId: string | undefined | null,
	payPeriodEndDate: string | undefined | null,
) =>
	useQuery({
		queryKey: [ASSIGN_AND_UNASSIGNED_EMPLOYEE_DATA_QUERY_KEY, clientId, selectedPayPeriodId, payPeriodEndDate],
		queryFn: (data) => {
			return PayrollService.getPayrollAdjustmentAddEmployeePanel(clientId, taxYear, selectedPayPeriodId, payPeriodEndDate);
		},
		refetchOnWindowFocus: false,
		enabled: false
	});

const useGetPayrollAdjustmentSettingEmployeeFields = (
	clientId: string,
	employeeId: string | null,
	payPeriodEndDate: string | null,
	payPeriodFrequencyId: string | null,
) =>
	useQuery({
		queryKey: [SETTING_EMPLOYEE_FIELDS_EMPLOYEE_DATA_QUERY_KEY, employeeId],
		queryFn: (data) => {
			return PayrollService.getPayrollAdjustmentSettingEmployeeFields(
				clientId,
				employeeId,
				payPeriodEndDate,
				payPeriodFrequencyId
			);
		},
		refetchOnWindowFocus: false
	});

const CLIENT_INFO_DATA_QUERY_KEY = "client-info-data-record";
const useGetClientInformationById = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [CLIENT_INFO_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: (data) => {
			return ClientService.getSimpleClientById(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const GENERAL_SETTING_INFO_DATA_QUERY_KEY = "general-settings-info-data-record";
const useGetGeneralSettingsData = (
	clientId: string,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [GENERAL_SETTING_INFO_DATA_QUERY_KEY, clientId, navigateToLogout],
		queryFn: () => {
			return GeneralSettingsService.getByClientId(clientId, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

export const PayrollAdjustment: React.FC<IPayroll> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	// const companyInfo = useSelector((state: IAppState) => state.dashboardApisData.client_detail);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const storedInvoiceNoId = useSelector((state: IAppState) => state.invoice.id);
	const navigateToLogout = useNavigateToLogout();
	const { AdminClientRoutes } = AdminClientRoutesComponent();
	const { ClientRoutes } = ClientRoutesComponent();

	const screenPermissions = useSelector((state: IAppState) => state.drawerMenu.screenPermissions);
	const payrollScreenPermissions = screenPermissions.find((x: any) => x.url === '/payroll/adjustment')


	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [loadingData, setIsLoading] = useState(false);
	const [selectedPayPeriodId, setSelectedPayPeriodId] = useState<null | string>(null);
	const [selectedpayPeriodIndex, setSelectedPayPeriodIndex] = useState<null | number>(null);
	const [payPeriodStarDate, setPayPeriodStarDate] = useState<null | string>(null);
	const [payPeriodEndDate, setPayPeriodEndDate] = useState<null | string>(null);
	const [showAddRemoveEmployees, setShowAddRemoveEmployees] = useState<boolean>(false);
	const [selectedRowsValues, setSelectedRowsValues] = useState([]);
	const [payrollSetupTableData, setPayrollAdjustmentTableData] =
		useState([]);
	const [remittanceFrequencyId, setRemittanceFrequencyId] = useState(undefined);


	const [addEmployee, setAddEmployee] = useState(false);
	const [settingEmployee, setSettingEmployee] = useState(false);
	const [selectedSettingEmployeeId, setSelectedSettingEmployeeId] = useState(null);
	const [moveEmployeeFirstPanelItems, setMoveEmployeeFirstPanelItems] =
		useState([]);
	const [moveEmployeeSecondPanelItems, setMoveEmployeeSecondPanelItems] =
		useState([]);

	const formMethods = useForm<any>({
		// resolver: yupResolver(ValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
	});



	const [queryParamsPayrollData, setQueryParamsPayrollData] = useState<IGetListRequest>({
		search_query: "",
		order_by: "firstName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});
	const [queryParamsCalculatedPayrollAdjustmentData, setQueryParamsCalculatedPayrollAdjustmentData] = useState<IGetListRequest>({
		search_query: "",
		order_by: "firstName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const { isPending: isPendingGeneralSettingsInformationById, data: general_settings } = useGetGeneralSettingsData(
		clientId,
		navigateToLogout
	);

	const { isPending: isPendingClientInformationById, data: companyInfo } = useGetClientInformationById(
		clientId,
		navigateToLogout
	);
	const { isPending: isPendingPayFrequencyDDL, data: payFrequencyDDLItems } =
		useGetPayFrequencyDDL(clientId, taxYear);

	useEffect(() => {
		if (payFrequencyDDLItems && payFrequencyDDLItems.length === 1) {
			setSelectedPayPeriodId(payFrequencyDDLItems[0].value)
			formMethods.setValue('payFrequencyDDLItems', payFrequencyDDLItems[0].value)
			let path = `/payroll/adjustment/${payFrequencyDDLItems[0].value}`
			navigate(path)
		}
	}, [payFrequencyDDLItems])

	const { id } = useParams();
	const { isPending, data } = useGetPayrollAdjustmentTableData(
		clientId,
		selectedPayPeriodId,
		payPeriodStarDate,
		payPeriodEndDate,
		queryParamsPayrollData,
		navigateToLogout
	);
	const payrollAdjustmentTableData: any | null = data;

	const {
		isPending: isPendingGetEmployeeDDLByStatus,
		data: edrEarningsAndTimeFormatData,
	} = useGetEDREarningsAndTimeFormatData(clientId, taxYear);


	const { isPending: isPendingCalculatedPayrollAdjustmentTableData, data: calculatedPayrollAdjustmentTableData } = useGetCalculatedPayrollAdjustmentTableData(
		clientId,
		selectedPayPeriodId,
		payPeriodStarDate,
		payPeriodEndDate,
		queryParamsCalculatedPayrollAdjustmentData,
		navigateToLogout
	);

	const {
		isPending: isPendingPayrollPeriodsDDL,
		data: payrollPeriodsDDL,
	} = useGetPayrollPeriodsDDL(clientId, selectedPayPeriodId, taxYear);
	let payrollPeriodsDDLWithAllProccessedPeriods = payrollPeriodsDDL && payrollPeriodsDDL[payrollPeriodsDDL.length - 1]?.process === null ? payrollPeriodsDDL.slice(0, -1) : payrollPeriodsDDL



	const {
		isPending: isPendingVocationalEarningsList,
		data: vocationalEarningsList,
	} = useGetVocationalEarningsList(clientId);

	// get call employee list assign/unassignn
	const {
		isLoading: isLoadingAssignedEmployees,
		data: assignedAddEmployeeResponsePayload,
		refetch: refetchAddEmployeePanels,
	} = useGetPayrollAdjustmentAddEmployeePanels(clientId, taxYear, selectedPayPeriodId, payPeriodEndDate);
	let assignedAddEmployeeResponseData: any = assignedAddEmployeeResponsePayload


	const {
		isLoading: isLoadingEmployeesFieldsData,
		data: employeeFieldsDataResponse,
		// refetch: refetchSettingEmployeePanels,
	} = useGetPayrollAdjustmentSettingEmployeeFields(
		clientId,
		selectedSettingEmployeeId,
		payPeriodEndDate,
		selectedPayPeriodId
	);
	let employeeFieldsDataResponseData: any = employeeFieldsDataResponse;
	console.log("employeeFieldsDataResponseData", employeeFieldsDataResponseData)


	const onClickAddEmployeesHandler = (value: any) => {
		setAddEmployee(true);
		refetchAddEmployeePanels();
	}
	const onCloseAddEmployeeHandler = () => {
		setAddEmployee(false);
		// setIsApiCalls(false);
	};
	const onClickSettingEmployeesHandler = (employeeId: any) => {
		// alert(employeeId)
		setSelectedSettingEmployeeId(employeeId)
		setSettingEmployee(true);
		// refetchSettingEmployeePanels();
	}
	const onCloseSettingEmployeeHandler = () => {
		setSettingEmployee(false);
		setSelectedSettingEmployeeId(null)
		// setIsApiCalls(false);
	};
	const refetchGetPayrollAdjustmentSettingEmployeeFields = () => {
		return Promise.all([
			queryClient.invalidateQueries({
				queryKey: [SETTING_EMPLOYEE_FIELDS_EMPLOYEE_DATA_QUERY_KEY, selectedSettingEmployeeId],
			}),
		]);
	};

	const moveEmployeeFirstPanelSelectionToolbar = useCallback(
		(selectedItems: any) => {

			setMoveEmployeeFirstPanelItems(selectedItems);
			return null;
		},
		[]
	);
	const moveEmployeeSecondPanelSelectionToolbar = useCallback(
		(selectedItems: any) => {

			setMoveEmployeeSecondPanelItems(selectedItems);
			return null;
		},
		[]
	);

	const [isLoadingAddEmployee, setIsLoadingAddEmployee] = useState(false)
	const [isLoadingRemoveEmployee, setIsLoadingRemoveEmployee] = useState(false)
	const onMoveAssignEmployeeClicked = () => {

		(async () => {
			try {
				setIsLoading(true);
				setIsLoadingAddEmployee(true);
				let selectedEmployeesIds = moveEmployeeFirstPanelItems.map((item: any) => item.employeeId);
				let payload = {
					clientId: clientId,
					employeeIds: selectedEmployeesIds,
					payPeriodEndDate: payPeriodEndDate,
					payrollSheduleId: selectedPayPeriodId,
				}
				let requestPayload = payload;


				const responce: any = await PayrollService.assignEmployees(requestPayload);
				if (responce.status === 201) {
					setSnackbarData({
						open: true,
						message: responce.data.message ? responce.data.message : `Employee has been added successfully!`,
						severity: "success",
					});
					refetchAddEmployeePanels();
					setMoveEmployeeFirstPanelItems([]);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [PAYROLL_ADJUSTMENT_LIST_QUERY_KEY, clientId,
								selectedPayPeriodId,
								payPeriodStarDate,
								payPeriodEndDate,
								queryParamsPayrollData,],
						}),
						queryClient.invalidateQueries({
							queryKey: [CALCULATED_PAYROLL_ADJUSTMENT_LIST_QUERY_KEY, clientId,
								selectedPayPeriodId,
								payPeriodStarDate,
								payPeriodEndDate,
								queryParamsCalculatedPayrollAdjustmentData,],
						}),
					]);
					// return queryClient.invalidateQueries({ queryKey: [ASSIGN_AND_UNASSIGNED_EMPLOYEE_DATA_QUERY_KEY, clientId, selectedPayPeriodId, payPeriodEndDate] });
				} else if (responce.status === 400) {
					setSnackbarData({
						open: true,
						message: responce.data.message ? responce.data.message : `Something went wrong!`,
						severity: "error",
					});
				} else {
					setSnackbarData({
						open: true,
						message: responce.message ? responce.message : `Something went wrong!`,
						severity: "error",
					});
				}
			} catch (error: any) {
				setSnackbarData({
					open: true,
					message: "Somthing went wrong!",
					severity: "error",
				});
				setIsLoading(false);
				setIsLoadingAddEmployee(false);
			} finally {
				setIsLoading(false);
				setIsLoadingAddEmployee(false);
			}
		})();
	};
	const onMoveUnAssignedEmployeeClicked = () => {

		(async () => {
			try {
				setIsLoading(true);
				setIsLoadingRemoveEmployee(true);
				let selectedEmployeesIds = moveEmployeeSecondPanelItems.map((item: any) => item.employeeId);
				let payload = {
					clientId: clientId,
					employeeIds: selectedEmployeesIds,
					payPeriodEndDate: payPeriodEndDate,
					payrollSheduleId: selectedPayPeriodId,
				}
				let requestPayload = payload;


				const responce: any = await PayrollService.unAssignEmployees(requestPayload);
				if (responce.status === 201) {
					setSnackbarData({
						open: true,
						message: responce.data.message ? responce.data.message : `Employee has been removed successfully!`,
						severity: "success",
					});
					refetchAddEmployeePanels();
					setMoveEmployeeSecondPanelItems([]);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [PAYROLL_ADJUSTMENT_LIST_QUERY_KEY, clientId,
								selectedPayPeriodId,
								payPeriodStarDate,
								payPeriodEndDate,
								queryParamsPayrollData,],
						}),
						queryClient.invalidateQueries({
							queryKey: [CALCULATED_PAYROLL_ADJUSTMENT_LIST_QUERY_KEY, clientId,
								selectedPayPeriodId,
								payPeriodStarDate,
								payPeriodEndDate,
								queryParamsCalculatedPayrollAdjustmentData,],
						}),
					]);
					// return queryClient.invalidateQueries({ queryKey: [ASSIGN_AND_UNASSIGNED_EMPLOYEE_DATA_QUERY_KEY] });
				} else if (responce.status === 400) {
					setSnackbarData({
						open: true,
						message: responce.data.message ? responce.data.message : `Something went wrong!`,
						severity: "error",
					});
				} else {
					setSnackbarData({
						open: true,
						message: responce.message ? responce.message : `Something went wrong!`,
						severity: "error",
					});
				}
			} catch (error: any) {
				setSnackbarData({
					open: true,
					message: "Somthing went wrong!",
					severity: "error",
				});
				setIsLoading(false);
				setIsLoadingRemoveEmployee(false);
			} finally {
				setIsLoadingRemoveEmployee(false);
				setIsLoading(false);
			}
		})();
	};

	useEffect(() => {
		if (payrollPeriodsDDL && payrollPeriodsDDLWithAllProccessedPeriods && payrollPeriodsDDLWithAllProccessedPeriods.length > 0) {
			// let lastItem = payrollPeriodsDDLWithAllProccessedPeriods.at(-1)
			// let lastOriginalPayPeriodItemItem = payrollPeriodsDDL.at(-1)


			// // Default Selected
			// const selectedPayrollPeriodIndex = payrollPeriodsDDLWithAllProccessedPeriods.findIndex((item: any) => item.value === lastItem.value);
			// formMethods.setValue('payrollPeriodsDDL', lastItem.endDate)
			// formMethods.setValue('payFrequencyDDLItems', lastItem.payScheduleId)
			// setSelectedPayPeriodIndex(selectedPayrollPeriodIndex)
			// setPayPeriodStarDate(lastItem.startDate)
			// setPayPeriodEndDate(lastItem.endDate)



			// if (lastOriginalPayPeriodItemItem.process) {
			// 	setShowAddRemoveEmployees(false)
			// } else {
			// 	setShowAddRemoveEmployees(true)
			// }
		} else {
			formMethods.setValue('payrollPeriodsDDL', undefined)
			setSelectedPayPeriodIndex(null)
			setPayPeriodStarDate(null)
			setPayPeriodEndDate(null)
			setShowAddRemoveEmployees(false)
		}
	}, [payrollPeriodsDDL])

	const { data: departmentsDDL, isPending: isPendingDepartmentDDL } =
		useGetDepartmentsDDL(clientId);


	const departmentsDDLItems = useMemo(() => {
		if (departmentsDDL && Array.isArray(departmentsDDL)) {
			let resources: any[] = sortDDLByLabel(departmentsDDL).map((resourceItem: any) => ({
				id: resourceItem.value,
				label: resourceItem.label,
				value: resourceItem.value,
			}));
			resources.unshift({
				label: "Department: ALL",
				value: "all",
			});
			return resources;
		} else if (departmentsDDL) {
			return [
				{
					label: "Department: ALL",
					value: "all",
				},
			];
		}
	}, [departmentsDDL]);



	const { isPending: isSubmitting, mutate: submitPayroll } = useMutation({
		mutationFn: PayrollService.payrollAdjustmentSaveData,

		onSuccess: (res: IPayroll | IServerError | IResponse | any) => {


			if (res && res.status === 201) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
					}),
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_ADJUSTMENT_LIST_QUERY_KEY, clientId,
							selectedPayPeriodId,
							payPeriodStarDate,
							payPeriodEndDate,
							queryParamsPayrollData,],
					}),
					queryClient.invalidateQueries({
						queryKey: [CALCULATED_PAYROLL_ADJUSTMENT_LIST_QUERY_KEY, clientId,
							selectedPayPeriodId,
							payPeriodStarDate,
							payPeriodEndDate,
							queryParamsCalculatedPayrollAdjustmentData,],
					}),
				]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { isPending: isCalculatingPayroll, mutate: calculatePayrollAdjustment } = useMutation({
		mutationFn: PayrollService.calculatePayrollAdjustment,

		onSuccess: (res: IPayroll | IServerError | IResponse | any) => {


			if (res && res.status === 201) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
					}),
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_ADJUSTMENT_LIST_QUERY_KEY, clientId,
							selectedPayPeriodId,
							payPeriodStarDate,
							payPeriodEndDate,
							queryParamsPayrollData,],
					}),
					queryClient.invalidateQueries({
						queryKey: [CALCULATED_PAYROLL_ADJUSTMENT_LIST_QUERY_KEY, clientId,
							selectedPayPeriodId,
							payPeriodStarDate,
							payPeriodEndDate,
							queryParamsCalculatedPayrollAdjustmentData,],
					}),
				]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { isPending: isInsertingEmployeePayrollData, mutate: insertEmployeePayrollAdjustmentData } = useMutation({
		mutationFn: PayrollService.insertEmployeePayrollAdjustmentData,

		onSuccess: (res: IPayroll | IServerError | IResponse | any) => {


			if (res && res.status === 201) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
					}),
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_ADJUSTMENT_LIST_QUERY_KEY, clientId,
							selectedPayPeriodId,
							payPeriodStarDate,
							payPeriodEndDate,
							queryParamsPayrollData,],
					}),
					queryClient.invalidateQueries({
						queryKey: [CALCULATED_PAYROLL_ADJUSTMENT_LIST_QUERY_KEY, clientId,
							selectedPayPeriodId,
							payPeriodStarDate,
							payPeriodEndDate,
							queryParamsCalculatedPayrollAdjustmentData,],
					}),
				]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { isPending: isEditingCalculatdePayrollAdjustment, mutate: editingCalculatdePayrollAdjustment } = useMutation({
		mutationFn: PayrollService.editingCalculatdePayrollAdjustment,

		onSuccess: (res: IPayroll | IServerError | IResponse | any) => {


			if (res && res.status === 200) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
					}),
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_ADJUSTMENT_LIST_QUERY_KEY, clientId,
							selectedPayPeriodId,
							payPeriodStarDate,
							payPeriodEndDate,
							queryParamsPayrollData,],
					}),
					queryClient.invalidateQueries({
						queryKey: [CALCULATED_PAYROLL_ADJUSTMENT_LIST_QUERY_KEY, clientId,
							selectedPayPeriodId,
							payPeriodStarDate,
							payPeriodEndDate,
							queryParamsCalculatedPayrollAdjustmentData,],
					}),
				]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	// const { isPending: isSavingCalculatdePayrollAdjustment, mutate: savingCalculatdePayrollAdjustment } = useMutation({
	// 	mutationFn: PayrollService.savingCalculatdePayrollAdjustment,

	// 	onSuccess: (res: IPayroll | IServerError | IResponse | any) => {


	// 		if (res && res.status === 200) {
	// 			setSnackbarData({
	// 				open: true,
	// 				message: "Success",
	// 				severity: "success",
	// 			});
	// 			return Promise.all([
	// 				queryClient.invalidateQueries({
	// 					queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
	// 				}),
	// 				queryClient.invalidateQueries({
	// 					queryKey: [PAYROLL_ADJUSTMENT_LIST_QUERY_KEY, clientId,
	// 						selectedPayPeriodId,
	// 						payPeriodStarDate,
	// 						payPeriodEndDate,
	// 						queryParamsPayrollData,],
	// 				}),
	// 				queryClient.invalidateQueries({
	// 					queryKey: [CALCULATED_PAYROLL_ADJUSTMENT_LIST_QUERY_KEY, clientId,
	// 						selectedPayPeriodId,
	// 						payPeriodStarDate,
	// 						payPeriodEndDate,
	// 						queryParamsCalculatedPayrollAdjustmentData,],
	// 				}),
	// 			]);
	// 		} else {
	// 			setSnackbarData({
	// 				open: true,
	// 				message:
	// 					res && res.message && res.message
	// 						? res.message
	// 						: "Something went wrong.",
	// 				severity: "error",
	// 			});
	// 		}
	// 	},
	// 	onError: (err) => {
	// 		// TODO: customize the toast message based on the response
	// 		setSnackbarData({
	// 			open: true,
	// 			message: "Something went wrong, try again later!",
	// 			severity: "error",
	// 		});
	// 	},
	// });
	const { isPending: isOverwriteCalculatdePayrollAdjustment, mutate: overwriteCalculatdePayrollAdjustment } = useMutation({
		mutationFn: PayrollService.overwriteCalculatdePayrollAdjustment,

		onSuccess: (res: IPayroll | IServerError | IResponse | any) => {


			if (res && res.status === 200) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
					}),
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_ADJUSTMENT_LIST_QUERY_KEY, clientId,
							selectedPayPeriodId,
							payPeriodStarDate,
							payPeriodEndDate,
							queryParamsPayrollData,],
					}),
					queryClient.invalidateQueries({
						queryKey: [CALCULATED_PAYROLL_ADJUSTMENT_LIST_QUERY_KEY, clientId,
							selectedPayPeriodId,
							payPeriodStarDate,
							payPeriodEndDate,
							queryParamsCalculatedPayrollAdjustmentData,],
					}),
				]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const [openEmployeePayrollDataDialog, setDialogEmployeePayrollDataOpen] = useState(false);
	const [openEmployeePayrollCalculatedDataDialog, setDialogEmployeePayrollCalculatedDataOpen] = useState(false);
	const [payrollDataGridSelectedRows, setPayrollAdjustmentDataGridSelectedRows] = useState([]);
	const [calculatedPayrollAdjustmentDataGridSelectedRows, setCalculatedPayrollAdjustmentDataGridSelectedRows] = useState([]);
	const [selectedIds, setSelectedIds] = useState([]);
	const [payrollSetupUpdatedItems, setPayrollUpdatedItems] =
		useState<any>(undefined);

	// for employee payroll data delete row
	const [singleEmployeePayrollDataDelete, setEmployeePayrollDataDelete] = useState<boolean>(false);
	// const [payrollRowData, setPayrollRowData] = useState<any>(0);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChangePayrollData = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParamsPayrollData(dispatchParams);
	}, []);
	const onRequestParamsChangeCalculatedPayrollAdjustmentData = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParamsCalculatedPayrollAdjustmentData(dispatchParams);
	}, []);

	const { isPending: isDeletingEmployeePayrollData, mutate: deleteEmployeePayrollAdjustmentDataById } =
		useMutation({
			mutationFn: PayrollService.delEmployeePayrollAdjustmentData,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the Payroll fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				return queryClient.invalidateQueries({
					queryKey: [PAYROLL_ADJUSTMENT_LIST_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deleteEmployeePayrollDataField = () => {
		if (singleEmployeePayrollDataDelete) {
			setEmployeePayrollDataDelete(false);
			if (payrollAdjustmentTableData && payrollAdjustmentTableData.rowSelection) {
				if (payrollDataGridSelectedRows.length > 0) {
					const empIds = payrollDataGridSelectedRows.map((x: any) => x.employeeId)

					let delEmployeePayrollDataPayload = {
						data: {
							clientId: clientId,
							payPeriodEndDate: payPeriodEndDate,
							payrollSheduleId: selectedPayPeriodId,
							employeeIds: empIds
						},
						navigateToLogout: navigateToLogout,
					};
					deleteEmployeePayrollAdjustmentDataById(delEmployeePayrollDataPayload);
					setDialogEmployeePayrollDataOpen(false);
				} else {
					setSnackbarData({
						open: true,
						message: "Please select at least one employee!",
						severity: "error"
					})
				}
			} else {
				const empIds = payrollSetupTableData.map((x: any) => x.employeeId)

				let delEmployeePayrollDataPayload = {
					data: {
						clientId: clientId,
						payPeriodEndDate: payPeriodEndDate,
						payrollSheduleId: selectedPayPeriodId,
						employeeIds: empIds
					},
					navigateToLogout: navigateToLogout,
				};
				deleteEmployeePayrollAdjustmentDataById(delEmployeePayrollDataPayload);
				setDialogEmployeePayrollDataOpen(false);
			}
		}
	};

	// const onRowDeleteClicked = useCallback((params: any) => {
	// 	setDialogEmployeePayrollDataOpen(true);
	// 	setEmployeePayrollDataDelete(true);
	// 	setPayrollRowData(params);
	// }, []);

	const onDeleteEmployeePayrollDataClicked = () => {
		setDialogEmployeePayrollDataOpen(true);
		setEmployeePayrollDataDelete(true);
		// if (selectedDataRecord) {
		// 	setAccountantId(selectedDataRecord.id);
		// }
	};

	// const onRowEditClicked = useCallback((params: any) => {
	// 	// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
	// 	let path = `/payroll/setup/${params.id}`;
	// 	navigate(path);
	// }, []);

	const onCloseEmployeePayrollDataDeleteHandler = () => {
		setDialogEmployeePayrollDataOpen(false);
	};

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const selectionToolbar = useCallback((selectedItems: any) => {
		let selectedItemsIds = selectedItems && selectedItems.map((item: any) => item.id);


		setPayrollAdjustmentDataGridSelectedRows(selectedItems);

		// if (selectedItems.length) {
		// 	return (
		// 		<></>
		// 	);
		// }
		return (
			<></>
		);
	}, []);
	const CalculatedPayrollAdjustmentDataGridSelectionToolbar = useCallback((selectedItems: any) => {
		let selectedItemsIds = selectedItems && selectedItems.map((item: any) => item.id);
		setCalculatedPayrollAdjustmentDataGridSelectedRows(selectedItems);
		return (
			<></>
		);
	}, []);
	const upadtedPayrollItems = useMemo(() => {
		if (payrollAdjustmentTableData && payrollAdjustmentTableData.items) {
			return payrollAdjustmentTableData.items;
		}
	}, [payrollAdjustmentTableData]);

	const upadtedCalculatedPayrollAdjustmentItems = useMemo(() => {
		if (calculatedPayrollAdjustmentTableData && calculatedPayrollAdjustmentTableData.items) {
			return calculatedPayrollAdjustmentTableData.items;
		}
	}, [calculatedPayrollAdjustmentTableData]);



	// const expandedLabels = ["Payroll"];
	// const expandedPayrollOrSetupItem = "Payroll"; // Special case for "List" under "Payroll"
	// const enhanceData = (data: any[], parentLabel?: string): any[] => {
	// 	return data.map((item) => {
	// 		let isExpanded = expandedLabels.includes(item.label);
	// 		if (
	// 			parentLabel === "Payroll" &&
	// 			item.label === expandedPayrollOrSetupItem
	// 		) {
	// 			isExpanded = true;
	// 		}
	// 		const enhancedItem = {
	// 			...item,
	// 			expanded: isExpanded,
	// 			items: item.items ? enhanceData(item.items, item.label) : undefined,
	// 		};
	// 		return enhancedItem;
	// 	});
	// };
	const expandedLabels = ["Payroll"];
	const expandedPayrollOrSetupItem = "Adjustment";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Payroll" &&
				item.label === expandedPayrollOrSetupItem
			) {
				isExpanded = true;
			}

			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedPayrollOrSetupItem && "/payroll/adjustment",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: PAYROLL_ADJUSTMENT_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);


		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);


	const onSubmit = (values: any) => {

	};

	useEffect(() => {
		if (id && !selectedPayPeriodId) {
			setSelectedPayPeriodId(id)
			formMethods.setValue('payFrequencyDDLItems', id)
		}
	}, [id])
	const onFrequencyChangeHandler = async (selectedItem: any) => {
		setSelectedPayPeriodId(selectedItem.value)
		let path = `/payroll/adjustment/${selectedItem.value}`
		navigate(path)
	};
	const onPayrollPeriodsDDLChangeHandler = async (selectedItem: any) => {
		const selectedpayrollPeriodValue = formMethods.watch("payrollPeriodsDDL");

		if (selectedpayrollPeriodValue && payrollPeriodsDDLWithAllProccessedPeriods) {
			const selectedPayrollPeriod = payrollPeriodsDDLWithAllProccessedPeriods && payrollPeriodsDDLWithAllProccessedPeriods.find((x: any) => x.endDate === selectedpayrollPeriodValue)
			const selectedPayrollPeriodIndex = payrollPeriodsDDLWithAllProccessedPeriods.findIndex((item: any) => item.value === selectedPayrollPeriod.value);

			setSelectedPayPeriodIndex(selectedPayrollPeriodIndex)
			setPayPeriodStarDate(selectedPayrollPeriod.startDate)
			setPayPeriodEndDate(selectedPayrollPeriod.endDate)

			let lastOriginalPayPeriodItemItem = payrollPeriodsDDL.at(-1)
			if (lastOriginalPayPeriodItemItem.process) {
				setShowAddRemoveEmployees(false)
			} else {
				setShowAddRemoveEmployees(true)
			}


		}
	};

	const buildCustomEarningsArray = (
		item: any,
		edrList: { label: string; value: string }[]
	) => {
		return edrList.map(edr => {
			const key = `customAmounts.${edr.label}`;

			return {
				id: edr.value,
				label: edr.label,
				value: Object.prototype.hasOwnProperty.call(item, key)
					? item[key]
					: item.customAmounts[edr.label]
			};
		});
	};

	const onSubmitButtonClickedHandler = () => {

		if (payrollAdjustmentTableData && payrollAdjustmentTableData.rowSelection) {
			if (payrollDataGridSelectedRows.length > 0) {
				const updatePayloadData =
					payrollDataGridSelectedRows &&
					payrollDataGridSelectedRows.map((item: any) => ({
						...item,
						clientId: clientId,
						payPeriodEndDate: payPeriodEndDate,
						payrollSheduleId: selectedPayPeriodId,
						vacationPayAccured: item.vacationPayAccured ? item.vacationPayAccured : item.vacationPayAccuredCalculateWhenVacationPayAccuredIsUndefined ?
							item.vacationPayAccuredCalculateWhenVacationPayAccuredIsUndefined : undefined,
						vacationPayAccuredCalculateWhenVacationPayAccuredIsUndefined: undefined,
						customEarnings: buildCustomEarningsArray(item, edrEarningsAndTimeFormatData.earnings),
						customDeductions: buildCustomEarningsArray(item, edrEarningsAndTimeFormatData.deductions),
						customReimbursements: buildCustomEarningsArray(item, edrEarningsAndTimeFormatData.reimbursements),
					}));



				submitPayroll({ payrollData: updatePayloadData })


				// this will use in calculate button

				// updatePayloadData.forEach((item, index) => {
				// 	const hasGross = item.grossIncomeIncome != null && item.grossIncome !== "";

				// 	// check if any of the hour fields have a non-null/non-empty value
				// 	const hasAnyHours =
				// 		(item.regularHours != null && item.regularHours !== "") ||
				// 		(item.ot1Dot5Hours != null && item.ot1Dot5Hours !== "") ||
				// 		(item.ot2Dot0Hours != null && item.ot2Dot0Hours !== "");

				// 	if (hasGross && !hasAnyHours) {
				// 		setSnackbarData({
				// 			open: true,
				// 			message: "hours are required with Gross!",
				// 			severity: "error"
				// 		})
				// 	} else {
				// 
				// 		// submitPayroll({ payrollData: updatePayloadData })
				// 	}
				// });
			} else {
				setSnackbarData({
					open: true,
					message: "Please select at least one employee!",
					severity: "error"
				})
			}
		} else {
			const updatePayloadData =
				payrollSetupTableData &&
				payrollSetupTableData.map((item: any) => ({
					...item,
					clientId: clientId,
					payPeriodEndDate: payPeriodEndDate,
					payrollSheduleId: selectedPayPeriodId,
					customEarnings: buildCustomEarningsArray(item, edrEarningsAndTimeFormatData.earnings),
					customDeductions: buildCustomEarningsArray(item, edrEarningsAndTimeFormatData.deductions),
					customReimbursements: buildCustomEarningsArray(item, edrEarningsAndTimeFormatData.reimbursements),
				}));

			submitPayroll({ payrollData: updatePayloadData })
		}
	};
	const onCalculatePayrollClicked = async () => {
		if (payrollAdjustmentTableData && payrollAdjustmentTableData.rowSelection) {
			if (payrollDataGridSelectedRows.length > 0) {

				const updatePayloadData =
					payrollDataGridSelectedRows &&
					payrollDataGridSelectedRows.map((item: any) => ({
						...item,
						clientId: clientId,
						payPeriodEndDate: payPeriodEndDate,
						payrollSheduleId: selectedPayPeriodId,
						customEarnings: buildCustomEarningsArray(item, edrEarningsAndTimeFormatData.earnings),
						customDeductions: buildCustomEarningsArray(item, edrEarningsAndTimeFormatData.deductions),
						customReimbursements: buildCustomEarningsArray(item, edrEarningsAndTimeFormatData.reimbursements),
					}));

				(async () => {
					try {
						setIsLoading(true)
						const response = await PayrollService.payrollAdjustmentSaveData({ payrollData: updatePayloadData });
						if (response && response.status === 201) {
							const empIds = payrollDataGridSelectedRows.map((x: any) => x.employeeId)
							let calculatePayrollAdjustmentDataPayload = {
								data: {
									clientId: clientId,
									payPeriodEndDate: payPeriodEndDate,
									payrollSheduleId: selectedPayPeriodId,
									employeeIds: empIds
								},
								navigateToLogout: navigateToLogout,
							};
							calculatePayrollAdjustment(calculatePayrollAdjustmentDataPayload);
						} else {
							setSnackbarData({
								open: true,
								message:
									response && response.message && response.message
										? response.message
										: "Something went wrong.",
								severity: "error",
							});
						}

					} catch { } finally { setIsLoading(false) }
				})();
			} else {
				setSnackbarData({
					open: true,
					message: "Please select at least one employee!",
					severity: "error"
				})
			}
		} else {
			const updatePayloadData =
				payrollSetupTableData &&
				payrollSetupTableData.map((item: any) => ({
					...item,
					clientId: clientId,
					payPeriodEndDate: payPeriodEndDate,
					payrollSheduleId: selectedPayPeriodId,
					customEarnings: buildCustomEarningsArray(item, edrEarningsAndTimeFormatData.earnings),
					customDeductions: buildCustomEarningsArray(item, edrEarningsAndTimeFormatData.deductions),
					customReimbursements: buildCustomEarningsArray(item, edrEarningsAndTimeFormatData.reimbursements),
				}));
			(async () => {
				try {
					setIsLoading(true)
					const response = await PayrollService.payrollAdjustmentSaveData({ payrollData: updatePayloadData });
					if (response && response.status === 201) {
						const empIds = payrollDataGridSelectedRows.map((x: any) => x.employeeId)
						let calculatePayrollAdjustmentDataPayload = {
							data: {
								clientId: clientId,
								payPeriodEndDate: payPeriodEndDate,
								payrollSheduleId: selectedPayPeriodId,
								employeeIds: empIds
							},
							navigateToLogout: navigateToLogout,
						};
						calculatePayrollAdjustment(calculatePayrollAdjustmentDataPayload);
					} else {
						setSnackbarData({
							open: true,
							message:
								response && response.message && response.message
									? response.message
									: "Something went wrong.",
							severity: "error",
						});
					}

				} catch { } finally { setIsLoading(false) }
			})();
		}
	};
	const onInsertButtonClicked = () => {

		if (payrollAdjustmentTableData && payrollAdjustmentTableData.rowSelection) {
			if (payrollDataGridSelectedRows.length > 0) {
				const empIds = payrollDataGridSelectedRows.map((x: any) => x.employeeId)

				let insertEmployeePayrollAdjustmentDataPayload = {
					data: {
						clientId: clientId,
						payPeriodEndDate: payPeriodEndDate,
						payrollSheduleId: selectedPayPeriodId,
						employeeIds: empIds,
						// insertingType: selectedItem === "Insert Gross Hours" ? 100 :
						// 	selectedItem === "Insert Gross" ? 200 :
						// 		selectedItem === "Insert Hours" ? 300 :
						// 			selectedItem === "Insert Previous" ? 400 :
						// 				selectedItem === "Insert EDR" ? 500 : selectedItem
					},
					navigateToLogout: navigateToLogout,
				};
				insertEmployeePayrollAdjustmentData(insertEmployeePayrollAdjustmentDataPayload);
			} else {
				setSnackbarData({
					open: true,
					message: "Please select at least one employee!",
					severity: "error"
				})
			}
		} else {
			const empIds = payrollSetupTableData.map((x: any) => x.employeeId)

			let insertEmployeePayrollAdjustmentDataPayload = {
				data: {
					clientId: clientId,
					payPeriodEndDate: payPeriodEndDate,
					payrollSheduleId: selectedPayPeriodId,
					employeeIds: empIds,
					// insertingType: selectedItem === "Insert Gross Hours" ? 100 :
					// 	selectedItem === "Insert Gross" ? 200 :
					// 		selectedItem === "Insert Hours" ? 300 :
					// 			selectedItem === "Insert Previous" ? 400 :
					// 				selectedItem === "Insert EDR" ? 500 : selectedItem
				},
				navigateToLogout: navigateToLogout,
			};
			insertEmployeePayrollAdjustmentData(insertEmployeePayrollAdjustmentDataPayload);
		}
	};





	// for calculated payroll buttons
	const onEditCalculatedPayrollAdjustmentButtonClicked = async () => {

		const selectedpayrollPeriodValue = formMethods.watch("payrollPeriodsDDL");
		if (selectedpayrollPeriodValue && payrollPeriodsDDLWithAllProccessedPeriods) {
			const selectedPayrollPeriod = payrollPeriodsDDLWithAllProccessedPeriods && payrollPeriodsDDLWithAllProccessedPeriods.find((x: any) => x.endDate === selectedpayrollPeriodValue)

			if (payrollAdjustmentTableData && payrollAdjustmentTableData.rowSelection) {
				if (calculatedPayrollAdjustmentDataGridSelectedRows.length > 0) {
					const updatePayloadData =
						calculatedPayrollAdjustmentDataGridSelectedRows &&
						calculatedPayrollAdjustmentDataGridSelectedRows.map((item: any) => ({
							...item,
							clientId: clientId,
							payPeriod: selectedPayrollPeriod.payPeriodCode,
							payPeriodEndDate: payPeriodEndDate,
							payrollSheduleId: selectedPayPeriodId,
						}));

					editingCalculatdePayrollAdjustment({
						clientId,
						data: { payrollData: updatePayloadData },
						navigateToLogout: navigateToLogout
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Please select at least one employee!",
						severity: "error"
					})
				}
			} else {
				const updatePayloadData =
					calculatedPayrollAdjustmentTableData &&
					calculatedPayrollAdjustmentTableData.map((item: any) => ({
						...item,
						clientId: clientId,
						payPeriod: selectedPayrollPeriod.payPeriodCode,
						payPeriodEndDate: payPeriodEndDate,
						payrollSheduleId: selectedPayPeriodId,
					}));
				editingCalculatdePayrollAdjustment({
					clientId,
					data: { payrollData: updatePayloadData },
					navigateToLogout: navigateToLogout
				});
			}
		}
	};
	// const onPrintCheckOrStubClaculatedOPayrollButtonClickedHandler = async (selectedItem: any) => {
	// 	// alert(selectedItem)
	// 	const empIds = calculatedPayrollAdjustmentDataGridSelectedRows.map((x: any) => x.employeeId)
	// 	if (selectedItem === 'Print Stub Only') {
	// 		if (calculatedPayrollAdjustmentDataGridSelectedRows && calculatedPayrollAdjustmentDataGridSelectedRows.length > 0) {
	// 			(async () => {
	// 				let payload = {
	// 					clientId: clientId,
	// 					empIds
	// 				}
	// 				try {
	// 					setIsLoading(true)
	// 					const response: any = await PayrollService.createStubPayrollPdfPrint(
	// 						payload
	// 					);
	// 					if (response.status === 400) {
	// 						setSnackbarData({
	// 							open: true,
	// 							message: response.data.message
	// 								? response.data.message
	// 								: "Something went wrong.",
	// 							severity: "error",
	// 						});
	// 					} else {
	// 						const filename = `StubPrint.pdf`;
	// 						savePdfAsFile(response, filename);
	// 					}
	// 				} catch { } finally { setIsLoading(false) }
	// 			})();
	// 		} else {
	// 			setSnackbarData({
	// 				open: true,
	// 				message: "Please select ateast one employee",
	// 				severity: "error",
	// 			})

	// 		}
	// 	}
	// };

	// const onPrintCheckOrStubClaculatedOPayrollButtonClickedHandler = async (): Promise<void> => {
	// 	const maskedStatus: any = sessionStorage.getItem("maskEnabled");
	// 	if (maskedStatus === "true") {
	// 		setSnackbarData({
	// 			open: true,
	// 			message: "Please unmask your data.",
	// 			severity: "error",
	// 		});
	// 	} else {

	// 		setIsLoading(true);
	// 		try {
	// 			const response: any = await PayrollService.createStubPayrollPdfPrint(
	// 				selectedRowsValues,
	// 				clientId,
	// 			);
	// 			if (response.status === 400) {
	// 				setSnackbarData({
	// 					open: true,
	// 					message: response.data.message
	// 						? response.data.message
	// 						: "Something went wrong.",
	// 					// message: "Please Unmask sensitive data for PDF printing.",
	// 					severity: "error",
	// 				});
	// 			} else {
	// 				const filename = `EmailStubPrint.pdf`;
	// 				savePdfAsFile(response, filename);
	// 			}
	// 			setIsLoading(false);
	// 		} catch (err) {
	// 			alert(err);
	// 		}
	// 		setIsLoading(false);

	// 	}
	// };


	// const onEmailStubCalculatedPayrollAdjustmentButtonClicked = async (selectedItem: any) => {
	// 	alert(selectedItem)
	// };


	// const onSubmitCalculatedPayrollAdjustmentButtonClickedHandler = () => {
	// 	const selectedpayrollPeriodValue = formMethods.watch("payrollPeriodsDDL");
	// 	if (selectedpayrollPeriodValue && payrollPeriodsDDL) {
	// 		const selectedPayrollPeriod = payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === selectedpayrollPeriodValue)
	// 		if (payrollAdjustmentTableData && payrollAdjustmentTableData.rowSelection) {
	// 			if (payrollDataGridSelectedRows.length > 0) {
	// 				const updatePayloadData =
	// 					payrollDataGridSelectedRows &&
	// 					payrollDataGridSelectedRows.map((item: any) => ({
	// 						...item,
	// 						clientId: clientId,
	// 						payPeriod: selectedPayrollPeriod.payPeriodCode,
	// 						payPeriodEndDate: payPeriodEndDate,
	// 						payrollSheduleId: selectedPayPeriodId,
	// 					}));
	// 				savingCalculatdePayrollAdjustment({
	// 					clientId,
	// 					data: { payrollData: updatePayloadData },
	// 					navigateToLogout: navigateToLogout
	// 				});
	// 			} else {
	// 				setSnackbarData({
	// 					open: true,
	// 					message: "Please select at least one employee!",
	// 					severity: "error"
	// 				})
	// 			}
	// 		} else {
	// 			const updatePayloadData =
	// 				calculatedPayrollAdjustmentTableData &&
	// 				calculatedPayrollAdjustmentTableData.map((item: any) => ({
	// 					...item,
	// 					clientId: clientId,
	// 					payPeriod: selectedPayrollPeriod.payPeriodCode,
	// 					payPeriodEndDate: payPeriodEndDate,
	// 					payrollSheduleId: selectedPayPeriodId,
	// 				}));
	// 			savingCalculatdePayrollAdjustment({
	// 				clientId,
	// 				data: { payrollData: updatePayloadData },
	// 				navigateToLogout: navigateToLogout
	// 			});
	// 		}
	// 	}
	// };
	const onSubmitOrOverWriteCalculatedPayrollAdjustmentButtonClickedHandler = (event: any) => {
		if (event === "Overwrite") {
			const selectedpayrollPeriodValue = formMethods.watch("payrollPeriodsDDL");
			if (selectedpayrollPeriodValue && payrollPeriodsDDLWithAllProccessedPeriods) {
				const selectedPayrollPeriod = payrollPeriodsDDLWithAllProccessedPeriods && payrollPeriodsDDLWithAllProccessedPeriods.find((x: any) => x.endDate === selectedpayrollPeriodValue)
				if (payrollAdjustmentTableData && payrollAdjustmentTableData.rowSelection) {
					if (calculatedPayrollAdjustmentDataGridSelectedRows.length > 0) {
						const updatePayloadData =
							calculatedPayrollAdjustmentDataGridSelectedRows &&
							calculatedPayrollAdjustmentDataGridSelectedRows.map((item: any) => ({
								...item,
								clientId: clientId,
								payPeriod: selectedPayrollPeriod.payPeriodCode,
								payPeriodEndDate: payPeriodEndDate,
								payrollSheduleId: selectedPayPeriodId,
							}));

						overwriteCalculatdePayrollAdjustment({
							clientId,
							data: { payrollData: updatePayloadData },
							navigateToLogout: navigateToLogout
						});
					} else {
						setSnackbarData({
							open: true,
							message: "Please select at least one employee!",
							severity: "error"
						})
					}
				} else {
					const updatePayloadData =
						calculatedPayrollAdjustmentTableData &&
						calculatedPayrollAdjustmentTableData.map((item: any) => ({
							...item,
							clientId: clientId,
							payPeriod: selectedPayrollPeriod.payPeriodCode,
							payPeriodEndDate: payPeriodEndDate,
							payrollSheduleId: selectedPayPeriodId,
						}));

					overwriteCalculatdePayrollAdjustment({
						clientId,
						data: { payrollData: updatePayloadData },
						navigateToLogout: navigateToLogout
					});
				}
			}
		} else {
			// const selectedpayrollPeriodValue = formMethods.watch("payrollPeriodsDDL");
			// if (selectedpayrollPeriodValue && payrollPeriodsDDL) {
			// 	const selectedPayrollPeriod = payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === selectedpayrollPeriodValue)
			// 	if (payrollAdjustmentTableData && payrollAdjustmentTableData.rowSelection) {
			// 		if (calculatedPayrollAdjustmentDataGridSelectedRows.length > 0) {
			// 			const updatePayloadData =
			// 				calculatedPayrollAdjustmentDataGridSelectedRows &&
			// 				calculatedPayrollAdjustmentDataGridSelectedRows.map((item: any) => ({
			// 					...item,
			// 					clientId: clientId,
			// 					payPeriod: selectedPayrollPeriod.payPeriodCode,
			// 					payPeriodEndDate: payPeriodEndDate,
			// 					payrollSheduleId: selectedPayPeriodId,
			// 					checkDate: formatDate(item.checkDate),
			// 				}));
			// 			
			// 			savingCalculatdePayrollAdjustment({
			// 				clientId,
			// 				data: { payrollData: updatePayloadData },
			// 				navigateToLogout: navigateToLogout
			// 			});
			// 		} else {
			// 			setSnackbarData({
			// 				open: true,
			// 				message: "Please select at least one employee!",
			// 				severity: "error"
			// 			})
			// 		}
			// 	} else {
			// 		const updatePayloadData =
			// 			calculatedPayrollAdjustmentTableData &&
			// 			calculatedPayrollAdjustmentTableData.map((item: any) => ({
			// 				...item,
			// 				clientId: clientId,
			// 				payPeriod: selectedPayrollPeriod.payPeriodCode,
			// 				payPeriodEndDate: payPeriodEndDate,
			// 				payrollSheduleId: selectedPayPeriodId,
			// 					checkDate: formatDate(item.checkDate),
			// 			}));
			// 		savingCalculatdePayrollAdjustment({
			// 			clientId,
			// 			data: { payrollData: updatePayloadData },
			// 			navigateToLogout: navigateToLogout
			// 		});
			// 	}
			// }
		}
	};
	const onProcessPayrollClicked = async () => {
		(async () => {
			const empIds = calculatedPayrollAdjustmentDataGridSelectedRows.map((x: any) => x.employeeId)
			let requestPayload = {
				clientId: clientId,
				payPeriodEndDate: payPeriodEndDate,
				payrollSheduleId: selectedPayPeriodId,
				employeeIds: empIds
			}
			try {
				setIsLoading(true)
				const response = await PayrollService.payrollAdjustmentPostEntry({ payrollData: requestPayload });
				if (response && response.status === 201) {
					(async () => {
						let requestPayload = {
							clientId: clientId,
							payPeriodFrequencyId: selectedPayPeriodId,
							payPeriodStarDate: payPeriodStarDate,
							payPeriodEndDate: payPeriodEndDate
						}
						try {
							setIsLoading(true)
							const response = await PayrollService.directDeposit({ payrollData: requestPayload });
						} catch { } finally { setIsLoading(false) }
					})();
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
					})
					// dispatch(setSelectedINVOICE(response.data));
					// const empIds = payrollDataGridSelectedRows.map((x: any) => x.employeeId)
					// let calculatePayrollAdjustmentDataPayload = {
					// 	data: {
					// 		clientId: clientId,
					// 		payPeriodEndDate: payPeriodEndDate,
					// 		payrollSheduleId: selectedPayPeriodId,
					// 		employeeIds: empIds
					// 	},
					// 	navigateToLogout: navigateToLogout,
					// };
					// calculatePayrollAdjustment(calculatePayrollAdjustmentDataPayload);
				} else {
					setSnackbarData({
						open: true,
						message:
							response && response.message && response.message
								? response.message
								: "Something went wrong.",
						severity: "error",
					});
				}

			} catch { } finally { setIsLoading(false) }
		})();
	};
	const onCancelProcessPayrollClicked = async () => {
		(async () => {
			let requestPayload = {
				clientId: clientId,
				invoiceNo: storedInvoiceNoId,
				payPeriodEndDate: payPeriodEndDate,
				payPeriodFrequencyId: selectedPayPeriodId,
			}

			try {
				setIsLoading(true)
				const response = await PayrollService.cancelProcess({ payrollData: requestPayload });
				if (response && response.status === 200) {
					queryClient.invalidateQueries({
						queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, selectedPayPeriodId, taxYear],
					})
					// dispatch(clearSelectedINVOICE());
					// const empIds = payrollDataGridSelectedRows.map((x: any) => x.employeeId)
					// let calculatePayrollAdjustmentDataPayload = {
					// 	data: {
					// 		clientId: clientId,
					// 		payPeriodEndDate: payPeriodEndDate,
					// 		payrollSheduleId: selectedPayPeriodId,
					// 		employeeIds: empIds
					// 	},
					// 	navigateToLogout: navigateToLogout,
					// };
					// calculatePayrollAdjustment(calculatePayrollAdjustmentDataPayload);
				} else {
					setSnackbarData({
						open: true,
						message:
							response && response.message && response.message
								? response.message
								: "Something went wrong.",
						severity: "error",
					});
				}

			} catch { } finally { setIsLoading(false) }
		})();
	};

	const onClickImportPayrollDataHandler = () => {
		alert('Comming Soon..')
	}

	return (
		<>
			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}

				sx={{
					mt: "1.7rem"
				}}>
				<BackdropCircularProgress
					open={isPending || isPendingPayrollPeriodsDDL || isSubmitting || isPendingPayFrequencyDDL || isPendingDepartmentDDL || isPendingCalculatedPayrollAdjustmentTableData || isDeletingEmployeePayrollData || isInsertingEmployeePayrollData || loadingData || isEditingCalculatdePayrollAdjustment || isOverwriteCalculatdePayrollAdjustment || isPendingVocationalEarningsList || isCalculatingPayroll || isPendingGetEmployeeDDLByStatus || isLoadingAssignedEmployees || isLoadingEmployeesFieldsData || isPendingClientInformationById || isPendingGeneralSettingsInformationById}
					testId="payroll-list"
				/>
				<DeleteConfirmationDialog
					open={openEmployeePayrollDataDialog}
					deleteActionHandler={deleteEmployeePayrollDataField}
					cancelActionHandler={onCloseEmployeePayrollDataDeleteHandler}
					testId="departments-confirmation-dialog"
				/>
				<FormProvider {...formMethods}>
					<form onSubmit={formMethods.handleSubmit(onSubmit)}>

						{/* Payroll Non-Calculated Data Table */}
						<PayrollAdjustmentSetupHeaderPanel
							payFrequencyDDLItems={payFrequencyDDLItems}
							onFrequencyChangeHandler={onFrequencyChangeHandler}
							payrollPeriodsDDL={payrollPeriodsDDL && payrollPeriodsDDLWithAllProccessedPeriods}
							onPayrollPeriodsDDLChangeHandler={onPayrollPeriodsDDLChangeHandler}
							departmentsDDLItems={departmentsDDLItems}
							payrollAdjustmentTableData={payrollAdjustmentTableData}
							selectedpayPeriodIndex={selectedpayPeriodIndex}
							payPeriodStarDate={payPeriodStarDate}
							payPeriodEndDate={payPeriodEndDate}
							showAddRemoveEmployees={showAddRemoveEmployees}
							onClickAddEmployeesHandler={onClickAddEmployeesHandler}
							summary={calculatedPayrollAdjustmentTableData && calculatedPayrollAdjustmentTableData.summary}
							onClickImportPayrollDataHandler={onClickImportPayrollDataHandler}
							general_settings={general_settings}
						/>
						{payrollAdjustmentTableData && payrollAdjustmentTableData.items && payrollAdjustmentTableData.items.length > 0 &&
							<PayrollAdjustmentDataGrid
								columns={payrollAdjustmentTableData ? payrollAdjustmentTableData.headerColumns : []}
								rows={
									payrollAdjustmentTableData && upadtedPayrollItems
										? upadtedPayrollItems
										: []
								}
								showIndexColumn={false}
								showChecboxColumn={payrollAdjustmentTableData && payrollAdjustmentTableData.rowSelection}
								// showSettingColumn={true}
								// onRowEditClicked={onRowEditClicked}
								// onRowDeleteClicked={onRowDeleteClicked}
								onSnackbarMessageData={(value: any) => setSnackbarData(value)}
								totalRecords={
									payrollAdjustmentTableData && payrollAdjustmentTableData.total
										? payrollAdjustmentTableData.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChangePayrollData}
								selectionToolbar={selectionToolbar}
								selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
								searchFieldPlaceholder={"Search..."}
								pageTitle={"Payroll"}
								PayrollAdjustmentTableData={(data: any) =>
									setPayrollAdjustmentTableData(data)
								}
								tableMargin={"0"}
								tableScrollHeight={true}
								vocationalEarningsList={vocationalEarningsList}
								onClickSettingEmployeesHandler={onClickSettingEmployeesHandler}
							/>}

						{payrollAdjustmentTableData && payrollAdjustmentTableData.items && payrollAdjustmentTableData.items.length > 0 && (
							<PayrollAdjustmentSetupToolbar
								payrollScreenPermissions={payrollScreenPermissions}
								onSubmitButtonClickedHandler={onSubmitButtonClickedHandler}
								onCalculatePayrollClicked={onCalculatePayrollClicked}
								onInsertButtonClicked={onInsertButtonClicked}
								onDeleteEmployeePayrollDataClicked={onDeleteEmployeePayrollDataClicked}
								payrollAdjustmentTableData={payrollAdjustmentTableData && payrollAdjustmentTableData}
								haveCalculatedTable={calculatedPayrollAdjustmentTableData && calculatedPayrollAdjustmentTableData.items && calculatedPayrollAdjustmentTableData.items.length > 0 ? true : false} />
						)}

						{/* Payroll Calculated Data Table */}
						<CalculatedPayrollAdjustmentSetupHeaderPanel
							departmentsDDLItems={departmentsDDLItems}
							calculatedPayrollAdjustmentTableData={calculatedPayrollAdjustmentTableData}
						/>
						{calculatedPayrollAdjustmentTableData && calculatedPayrollAdjustmentTableData.items && calculatedPayrollAdjustmentTableData.items.length > 0 &&
							<CalculatedPayrollAdjustmentDataGrid
								columns={calculatedPayrollAdjustmentTableData ? calculatedPayrollAdjustmentTableData.headerColumns : []}
								rows={
									calculatedPayrollAdjustmentTableData && upadtedCalculatedPayrollAdjustmentItems
										? upadtedCalculatedPayrollAdjustmentItems
										: []
								}
								showIndexColumn={false}
								showChecboxColumn={payrollAdjustmentTableData && payrollAdjustmentTableData.rowSelection}
								onSnackbarMessageData={(value: any) => setSnackbarData(value)}
								totalRecords={
									payrollAdjustmentTableData && payrollAdjustmentTableData.total
										? payrollAdjustmentTableData.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChangeCalculatedPayrollAdjustmentData}
								selectionToolbar={CalculatedPayrollAdjustmentDataGridSelectionToolbar}
								selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
								searchFieldPlaceholder={"Search..."}
								pageTitle={"Payroll"}
								PayrollAdjustmentTableData={(data: any) =>
									setPayrollAdjustmentTableData(data)
								}
								tableMargin={"0"}
								tableScrollHeight={true}
							/>}

						{calculatedPayrollAdjustmentTableData && calculatedPayrollAdjustmentTableData.items && calculatedPayrollAdjustmentTableData.items.length > 0 && (
							<CalculatedPayrollAdjustmentSetupToolbar
								// onPrintCheckOrStubClaculatedOPayrollButtonClickedHandler={onPrintCheckOrStubClaculatedOPayrollButtonClickedHandler}
								onEditCalculatedPayrollAdjustmentButtonClicked={onEditCalculatedPayrollAdjustmentButtonClicked}
								// onEmailStubCalculatedPayrollAdjustmentButtonClicked={onEmailStubCalculatedPayrollAdjustmentButtonClicked}
								onSubmitOrOverWriteCalculatedPayrollAdjustmentButtonClickedHandler={onSubmitOrOverWriteCalculatedPayrollAdjustmentButtonClickedHandler}
								onProcessPayrollClicked={onProcessPayrollClicked}
								onCancelProcessPayrollClicked={onCancelProcessPayrollClicked}
								payrollNonCalculatedDataItems={payrollAdjustmentTableData && payrollAdjustmentTableData.items}
								selectedPayPeriod={payrollPeriodsDDL && payrollPeriodsDDLWithAllProccessedPeriods.find((x: any) => x.endDate === formMethods.watch("payrollPeriodsDDL"))}
								payPeriodEndDate={payPeriodEndDate}
								payPeriodStartDate={payPeriodStarDate}
								companyInfo={companyInfo}
								calculatedPayrollAdjustmentTableData={calculatedPayrollAdjustmentTableData}

							/>
						)}
					</form>
				</FormProvider>
			</Grid>

			<EnhancedDialog
				dialogMaxWidth={1250}
				dialogTitle="Employee(s)"
				key="Payroll Add Employees"
				testId="Payroll-Add-Employees"
				onCloseHandler={onCloseAddEmployeeHandler}
				open={addEmployee}
				dialogContent={
					<PayrollAdjustmentAddEmployeePanel
						moveEmployeeFirstPanelSelectionToolbar={moveEmployeeFirstPanelSelectionToolbar}
						moveEmployeeSecondPanelSelectionToolbar={moveEmployeeSecondPanelSelectionToolbar}
						assignedAddEmployeeResponsePayload={assignedAddEmployeeResponsePayload}
						assignedAddEmployeeResponseData={assignedAddEmployeeResponseData}
						onMoveAssignEmployeeClicked={onMoveAssignEmployeeClicked}
						onMoveUnAssignedEmployeeClicked={onMoveUnAssignedEmployeeClicked}
						isLoadingAddEmployee={isLoadingAddEmployee}
						isLoadingRemoveEmployee={isLoadingRemoveEmployee}
						moveEmployeeFirstPanelItems={moveEmployeeFirstPanelItems}
						moveEmployeeSecondPanelItems={moveEmployeeSecondPanelItems}
					/>
					// <Grid
					// 	container
					// 	columnSpacing={2}
					// 	size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
					// 	sx={{ width: "100%" }}
					// >
					// 	<Grid size={{ xs: 5, sm: 5, md: 5, lg: 5, xl: 5 }}>
					// 		<Box>
					// 			<MoveEmployeeFirstPanel
					// 				columns={
					// 					assignedAddEmployeeResponsePayload && moveEmployeeFirstPanelUpdatedColumns
					// 				}
					// 				rows={
					// 					assignedAddEmployeeResponsePayload &&
					// 					// assignedAddEmployeeResponseData.assigned.length !== 0 &&
					// 					assignedAddEmployeeResponseData.assigned
					// 				}
					// 				selectionToolbar={moveEmployeeFirstPanelSelectionToolbar}
					// 			/>
					// 		</Box>
					// 	</Grid>
					// 	<Grid
					// 		size={{ xs: 2, sm: 2, md: 2, lg: 2, xl: 2 }}
					// 		sx={{
					// 			display: "flex",
					// 			justifyContent: "center",
					// 			alignItems: "center",
					// 		}}
					// 	>
					// 		<Box sx={{ display: "flex", flexDirection: "column" }}>
					// 			<EnhancedButtonPrimeReact
					// 				// icon={isLoadingAddOrRemoveEmployee && </>}
					// 				onClickHandler={onMoveAssignEmployeeClicked}
					// 				loading={isLoadingAddEmployee}
					// 				// size="small"
					// 				label="Add"
					// 				severity={EnhancedButtonPrimeReactSeverity.Success}
					// 				raised
					// 				disabled={
					// 					moveEmployeeFirstPanelItems &&
					// 						moveEmployeeFirstPanelItems.length > 0
					// 						? false
					// 						: true
					// 				}
					// 				style={{ justifyContent: "center" }}
					// 			/>
					// 			<br />
					// 			<EnhancedButtonPrimeReact
					// 				// icon="pi pi-angle-double-left"
					// 				onClickHandler={onMoveUnAssignedEmployeeClicked}
					// 				loading={isLoadingRemoveEmployee}
					// 				// size="small"
					// 				label="Remove"
					// 				severity={EnhancedButtonPrimeReactSeverity.Danger}
					// 				raised
					// 				disabled={
					// 					moveEmployeeSecondPanelItems &&
					// 						moveEmployeeSecondPanelItems.length > 0
					// 						? false
					// 						: true
					// 				}
					// 			/>
					// 		</Box>
					// 	</Grid>
					// 	<Grid size={{ xs: 5, sm: 5, md: 5, lg: 5, xl: 5 }}>
					// 		<Box>
					// 			<MoveEmployeeSecondPanel
					// 				columns={
					// 					assignedAddEmployeeResponsePayload && moveEmployeeFirstPanelUpdatedColumns
					// 				}
					// 				rows={
					// 					assignedAddEmployeeResponsePayload &&
					// 					// assignedAddEmployeeResponseData.unassigned.length !== 0 &&
					// 					assignedAddEmployeeResponseData.unassigned
					// 				}
					// 				selectionToolbar={moveEmployeeSecondPanelSelectionToolbar}
					// 			/>
					// 		</Box>
					// 	</Grid>
					// </Grid>
				}
				showdividers={false}
				dialogAction={null}
			/>


			{selectedSettingEmployeeId && employeeFieldsDataResponseData && <EnhancedDialog
				dialogMaxWidth={1250}
				dialogTitle="Employee Settings"
				key="Employees Settings"
				testId="Payroll-Add-Employees"
				onCloseHandler={onCloseSettingEmployeeHandler}
				open={settingEmployee}
				dialogContent={
					<PayrollAdjustmentSettingEmployeeDataTable
						rows={[employeeFieldsDataResponseData]}
						onSnackbarMessageData={(value: any) => setSnackbarData(value)}
						onSaveAndCloseSettingEmployeeHandler={onCloseSettingEmployeeHandler}
						refetchGetAPI={refetchGetPayrollAdjustmentSettingEmployeeFields}
					/>
				}
				showdividers={false}
				dialogAction={null}
			/>}
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
